import { useState } from "react";
import {
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Badge,
  Chip,
  Avatar,
  Typography,
  Fade,
  Grow,
  Slide,
  keyframes
} from "@mui/material";
import { 
  Dashboard, 
  Logout, 
  People, 
  AccountBalance, 
  NotificationImportant,
  Analytics,
  TrendingUp,
  Assessment
} from "@mui/icons-material";
import { useCookies } from "react-cookie";
import { useNavigate, useLocation } from "react-router-dom";

// Custom animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0) scale(1); }
  40% { transform: translateY(-10px) scale(1.1); }
  60% { transform: translateY(-5px) scale(1.05); }
`;

const slideIn = keyframes`
  0% { transform: translateX(-10px); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.pathname);

  const [, , removeCookie] = useCookies(["auth_token"]);
  const logout = () => {
    let date = new Date();
    date.setDate(1);
    date.setMonth(1);
    date.setFullYear(1960);
    removeCookie("auth_token", { path: "/", expires: date });
    navigate("/");
  };

  const navigationItems = [
    {
      id: '/customer/dashboard',
      label: 'Dashboard',
      icon: <Dashboard />,
      color: '#4CAF50',
      subtext: 'My Overview'
    },
    {
      id: '/customer/loans',
      label: 'My Loans',
      icon: <AccountBalance />,
      color: '#2196F3',
      subtext: 'Loan Management'
    }
  ];

  return (
    <AppBar
      position="static"
      sx={{ 
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        borderRadius: "0 0 20px 20px",
        mx: 2,
        mt: 1,
        width: "calc(100% - 32px)"
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ 
              background: "linear-gradient(45deg, #667eea, #764ba2)",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)"
            }}>
              💸
            </Avatar>
            <Fade in={true} timeout={1000}>
              <Typography variant="h6" sx={{ 
                color: "white", 
                fontWeight: "bold",
                background: "linear-gradient(45deg, #fff, #f0f0f0)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text"
              }}>
                Loan Portal
              </Typography>
            </Fade>
          </Box>

          <List sx={{ display: "flex", gap: 1 }}>
            {navigationItems.map((item, index) => {
              const isActive = activeTab === item.id;
              return (
                <ListItem key={item.id} disablePadding>
                  <Tooltip title={item.label} placement="bottom">
                    <ListItemButton
                      onClick={() => {
                        setActiveTab(item.id);
                        navigate(item.id);
                      }}
                      sx={{
                        borderRadius: "15px",
                        mx: 0.5,
                        background: isActive ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(10px)",
                        border: isActive ? "2px solid rgba(255,255,255,0.3)" : "2px solid transparent",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform: isActive ? "scale(1.05)" : "scale(1)",
                        position: "relative",
                        overflow: "hidden",
                        "&:hover": {
                          background: "rgba(255,255,255,0.15)",
                          transform: "scale(1.08)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                          transition: "left 0.5s",
                        },
                        "&:hover::before": {
                          left: "100%",
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: isActive ? "white" : "rgba(255,255,255,0.9)",
                        transition: "all 0.3s ease",
                        transform: isActive ? "scale(1.2)" : "scale(1)"
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <Slide direction="right" in={isActive} timeout={300}>
                        <ListItemText 
                          primary={item.label}
                          sx={{ 
                            color: "white",
                            "& .MuiListItemText-primary": {
                              fontSize: "0.95rem",
                              fontWeight: isActive ? "bold" : "medium"
                            }
                          }}
                        />
                      </Slide>
                      {isActive && (
                        <Chip 
                          label="Active" 
                          size="small" 
                          sx={{ 
                            background: "rgba(255,255,255,0.2)",
                            color: "white",
                            border: "1px solid rgba(255,255,255,0.3)",
                            ml: 1,
                            animation: `${pulse} 2s infinite`
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Badge badgeContent={3} color="error" sx={{ "& .MuiBadge-badge": { animation: `${pulse} 2s infinite` } }}>
            <ListItemButton 
              onClick={logout}
              sx={{
                borderRadius: "15px",
                background: "rgba(255,255,255,0.1)",
                border: "2px solid rgba(255,255,255,0.2)",
                color: "white",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(45deg, #ff6b6b, #ff8e8e)",
                  transform: "scale(1.05)",
                  boxShadow: "0 8px 25px rgba(255,107,107,0.3)"
                }
              }}
            >
              <Logout sx={{ mr: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                Logout
              </Typography>
            </ListItemButton>
          </Badge>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const AdminNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.pathname);

  const [, , removeCookie] = useCookies(["auth_token"]);
  const logout = () => {
    let date = new Date();
    date.setDate(1);
    date.setMonth(1);
    date.setFullYear(1960);
    removeCookie("auth_token", { path: "/", expires: date });
    navigate("/");
  };

  const adminNavigationItems = [
    {
      id: '/admin/dashboard',
      label: 'Dashboard',
      icon: <Analytics />,
      color: '#4CAF50',
      notification: false,
      subLabel: 'Business Overview'
    },
    {
      id: '/admin/customers',
      label: 'Customers',
      icon: <People />,
      color: '#2196F3',
      notification: false,
      subLabel: 'Manage Clients'
    },
    {
      id: '/admin/reminders',
      label: 'Reminders',
      icon: <NotificationImportant />,
      color: '#E91E63',
      notification: true,
      subLabel: 'Payment Alerts'
    },
    {
      id: '/admin/cashflow',
      label: 'Cashflow',
      icon: <TrendingUp />,
      color: '#9C27B0',
      notification: false,
      subLabel: 'Financial Reports'
    },
    {
      id: '/admin/reports',
      label: 'Reports',
      icon: <Assessment />,
      color: '#FF9800',
      notification: false,
      subLabel: 'Monthly Analytics'
    }
  ];

  return (
    <AppBar
      position="static"
      sx={{ 
        background: "linear-gradient(135deg, #2C3E50 0%, #4A6741 50%, #1abc9c 100%)",
        boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: "20px",
        mx: 2,
        mt: 1,
        width: "calc(100% - 32px)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(135deg, rgba(255,255,255,0.1), transparent)",
          pointerEvents: "none",
        }
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", py: 2, px: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Fade in={true} timeout={1500}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ 
                width: 48,
                height: 48,
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
                fontSize: "1.5rem"
              }}>
                📊
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ 
                  color: "white", 
                  fontWeight: "bold",
                  background: "linear-gradient(45deg, #fff, #f8ffd9)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text"
                }}>
                  Admin Console
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                  Business Intelligence Panel
                </Typography>
              </Box>
            </Box>
          </Fade>

          <List sx={{ 
            display: "flex", 
            gap: 1,
            flexWrap: "nowrap",
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none"
          }}>
            {adminNavigationItems.map((item, index) => {
              const isActive = activeTab === item.id;
              return (
                <ListItem key={item.id} disablePadding>
                  <Tooltip 
                    title={
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                          {item.label}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {item.subLabel}
                        </Typography>
                      </Box>
                    } 
                    placement="bottom"
                      arrow
                  >
                    <ListItemButton
                      onClick={() => {
                        setActiveTab(item.id);
                        navigate(item.id);
                      }}
                      sx={{
                        borderRadius: "20px",
                        mx: 0.5,
                        background: isActive 
                          ? "linear-gradient(135deg, rgba(255,255,255,0.3), rgba(255,255,255,0.1))" 
                          : "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(15px)",
                        border: isActive 
                          ? "2px solid rgba(255,255,255,0.4)" 
                          : "2px solid transparent",
                        transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                        transform: isActive ? "scale(1.08)" : "scale(1)",
                        position: "relative",
                        overflow: "hidden",
                        minWidth: "120px",
                        "&:hover": {
                          background: "linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05))",
                          transform: "scale(1.12)",
                          boxShadow: "0 12px 35px rgba(0,0,0,0.15)",
                        },
                        "&:hover::after": {
                          opacity: 1,
                        },
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          width: "150%",
                          height: "150%",
                          background: `radial-gradient(circle, ${item.color}30, transparent 30%)`,
                          transform: "translate(-50%, -50%)",
                          opacity: 0,
                          transition: "all 0.6s ease",
                          zIndex: -1,
                        },
                      }}
                    >
                      <Box sx={{ position: "relative", width: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ position: "relative" }}>
                            <ListItemIcon sx={{ 
                              color: isActive ? "white" : "rgba(255,255,255,0.9)",
                              transition: "all 0.4s ease",
                              transform: isActive ? "scale(1.3) rotate(5deg)" : "scale(1)",
                            }}>
                              {item.notification ? (
                                <Badge 
                                  badgeContent="3" 
                                  color="error"
                                  sx={{
                                    "& .MuiBadge-badge": { 
                                      animation: `${pulse} 2s infinite`,
                                      fontSize: "0.7rem",
                                      height: "16px",
                                      minWidth: "16px"
                                    }
                                  }}
                                >
                                  {item.icon}
                                </Badge>
                              ) : (
                                item.icon
                              )}
                            </ListItemIcon>
                          </Box>
                          
                          <Box sx={{ 
                            display: isActive ? "block" : "none",
                            animation: `${slideIn} 0.3s ease`
                          }}>
                            <Slide direction="left" in={isActive} timeout={400}>
                              <Box>
                                <Typography variant="body2" sx={{ 
                                  color: "white",
                                  fontWeight: isActive ? "bold" : "medium",
                                  fontSize: "0.9rem"
                                }}>
                                  {item.label}
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                  color: "rgba(255,255,255,0.8)",
                                  fontSize: "0.7rem",
                                  display: "block"
                                }}>
                                  {item.subLabel}
                                </Typography>
                              </Box>
                            </Slide>
                          </Box>
                        </Box>

                        {isActive && (
                          <Grow in={isActive} timeout={600}>
                            <Chip 
                              label="ACTIVE" 
                              size="small"
                              sx={{ 
                                position: "absolute",
                                top: -8,
                                right: -8,
                                background: `linear-gradient(135deg, ${item.color}, ${item.color}cc)`,
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "0.6rem",
                                height: "20px",
                                animation: `${bounce} 1s infinite`,
                                boxShadow: "0 2px 10px rgba(0,0,0,0.2)"
                              }}
                            />
                          </Grow>
                        )}
                      </Box>
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Badge badgeContent="5" color="error" sx={{ 
            "& .MuiBadge-badge": { 
              animation: `${pulse} 2s infinite`,
              fontSize: "0.7rem"
            }
          }}>
            <ListItemButton 
              onClick={logout}
              sx={{
                borderRadius: "20px",
                background: "linear-gradient(135deg, rgba(255,107,107,0.8), rgba(255,154,154,0.6))",
                backdropFilter: "blur(15px)",
                border: "2px solid rgba(255,255,255,0.3)",
                color: "white",
                transition: "all 0.4s ease",
                px: 3,
                py: 1.5,
                "&:hover": {
                  background: "linear-gradient(135deg, #ff6b6b, #ff8e8e)",
                  transform: "scale(1.08) translateY(-2px)",
                  boxShadow: "0 15px 40px rgba(255,107,107,0.4)"
                }
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Logout sx={{ fontSize: "1.1rem" }} />
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Sign Out
                </Typography>
              </Box>
            </ListItemButton>
          </Badge>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
export { Navbar, AdminNavbar };
